import {
  enrollOrganization,
  IEnrollOrganizationData,
} from './enrollOrganizationSlice';
import React, {
  useState,
  useEffect,
  useCallback,
  FormEvent,
  useMemo,
} from 'react';
import { useDispatch } from 'react-redux';
import { verifyToken } from './verifyTokenSlice';
import {
  selector as enrollOrganizationSelector,
  updateQueryParams,
} from './enrollOrganizationSlice';
import { selector as verifyTokenSelector } from './verifyTokenSlice';
import { useSelector } from 'react-redux';
import { Status } from '../../common/constants';
import { useSearchParams } from 'react-router-dom';

const useOrgEnrollment = () => {
  const dispatch = useDispatch();
  const [isVerified, setIsVerified] = useState<boolean>(false);
  const [isEnrolled, setIsEnrolled] = useState<boolean>(false);
  const [isEnrollmentFailed, setIsEnrollmentFailed] = useState<boolean>(false);
  const [email, setEmail] = useState<string>('');
  const [name, setName] = useState<string>('');
  const [phoneNo, setPhoneNo] = useState<string>('');
  const [address, setAddress] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [passwordError, setPasswordError] = useState<string>('');
  const [confirmPasswordError, setConfirmPasswordError] = useState<string>('');
  const [isPasswordEnable, setIsPasswordEnable] = useState<boolean>(false);
  const [adminName, setAdminName] = useState<string>('');
  const enrollOrgApiStatus = useSelector(enrollOrganizationSelector);
  const verifyTokenApiResponse = useSelector(verifyTokenSelector);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [searchParams] = useSearchParams();

  // we are extracting token from query parameter and sending it to registerOrg api
  useEffect(() => {
    const token = searchParams?.get('token');
    dispatch(updateQueryParams(token));
  }, [searchParams, dispatch]);

  useEffect(() => {
    if (
      enrollOrgApiStatus.status === Status.PENDING ||
      verifyTokenApiResponse.status === Status.PENDING
    ) {
      setIsLoading(true);
    } else {
      setIsLoading(false);
    }
  }, [enrollOrgApiStatus.status, verifyTokenApiResponse.status]);

  const handleVerifyToken = useCallback(async () => {
    const verificationToken = new URLSearchParams(window.location.search).get(
      'token'
    );
    if (verificationToken && !isVerified) {
      dispatch(verifyToken(verificationToken));
    }
  }, [dispatch, isVerified]);

  const validatePassword = (value: string): void => {
    const passwordRegex =
      /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    if (!passwordRegex.test(value)) {
      setPasswordError(
        'Password must be at least 8 characters, include a number, and a special character.'
      );
    } else {
      setPasswordError('');
    }
  };

  const handlePasswordChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const value = e.target.value;
    setPassword(value);
    validatePassword(value);
  };

  const handleConfirmPasswordChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const value = e.target.value;
    setConfirmPassword(value);
    if (value !== password) {
      setConfirmPasswordError('Passwords do not match.');
    } else {
      setConfirmPasswordError('');
    }
  };

  const handleOrgAdminEnrollment = useCallback(
    (e: FormEvent) => {
      e.preventDefault();
      const payload: IEnrollOrganizationData = {
        name: name,
        email: email,
        phoneNo: phoneNo,
        address: address,
        adminName: adminName,
        isPasswordEnable: isPasswordEnable,
        password: password,
      };
      dispatch(enrollOrganization(payload));
    },
    [dispatch, name, email, phoneNo, address, adminName]
  );

  const isTokenVerificationFailed = useMemo((): boolean => {
    return verifyTokenApiResponse.status === Status.FAILED;
  }, [verifyTokenApiResponse.status]);

  useEffect(() => {
    handleVerifyToken();
  }, [handleVerifyToken]);

  useEffect(() => {
    if (enrollOrgApiStatus.status === Status.SUCCESS) {
      setIsEnrolled(true);
      setAddress('');
      setEmail('');
      setName('');
      setPhoneNo('');
    } else if (enrollOrgApiStatus.status === Status.FAILED) {
      setIsEnrollmentFailed(true);
    }
  }, [enrollOrgApiStatus.status]);

  useEffect(() => {
    if (verifyTokenApiResponse.status === Status.SUCCESS) {
      setIsVerified(true);
      setEmail(verifyTokenApiResponse.adminDetails.email);
      setName(verifyTokenApiResponse.adminDetails.name);
    }
  }, [verifyTokenApiResponse.status]);

  return {
    isTokenVerificationFailed,
    handleOrgAdminEnrollment,
    isEnrolled,
    email,
    name,
    phoneNo,
    address,
    password,
    confirmPassword,
    isPasswordEnable,
    passwordError,
    confirmPasswordError,
    setEmail,
    setName,
    setPhoneNo,
    setAddress,
    setPassword,
    setConfirmPassword,
    setIsPasswordEnable,
    setPasswordError,
    setConfirmPasswordError,
    setAdminName,
    isVerified,
    isEnrollmentFailed,
    isLoading,
    enrollOrgApiStatus,
    handlePasswordChange,
    handleConfirmPasswordChange,
  };
};

export default useOrgEnrollment;
